import { Typography } from '@mui/material';
import { GridRowModel } from '@mui/x-data-grid-premium';
import { cloneDeep, isEqual } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { CalculationType, CostLine, LineCalculationInfo, Money } from 'sr-types/lib/opportunity/v1/graphql';
import { Rate } from 'sr-types/lib/search/v1/graphql';
import { FEATURE_CONSTANTS } from '../../constants/featureConstants';
import { useL10n } from '../../hooks/useL10n';
import I18n from '../../i18n/I18n';
import DataGrid from '../../list/DataGrid';
import { isMoney, moneyToBigNumber } from '../../utils/money';
import { DiscountCalculationTypesEnum, adjustmentsToRows, discountLevelTypes } from './costLineAdapter';
import useCalculationTypeColDef from './useCalculationTypeColDef';
import useCalculator, { CostCalculator, getSumOfDiscounts } from './useCalculator';
import useCostLineAdapter from './useCostLineAdapter';
import useDiscountColDef from './useDiscountColDef';
import useMoneyColDef from './useMoneyColDef';
import usePricingRowValidation from './usePricingRowValidation';

export interface AdjustmentLine {
    id: number;
    itemPrice: Money;
    calculationType: string;
    baseTotal: Money;
    itemPercent: number;
    adjustmentType: string;
    discounts: number;
}
const useDiscountGridColumns = (
    name,
    locale,
    calculationTypes: CalculationType[],
    rows: AdjustmentLine[],
    setRows: (newVal: any[]) => void,
    calculator: CostCalculator,
    shouldCalculate: boolean,
    rates: Rate,
    discountSetting
) => {
    const { setIsRowValid, isRowValid } = usePricingRowValidation();

    const onChange = async (id, apiRef, calculationType) => {
        await apiRef.current.setEditCellValue({
            id: id,
            field: 'itemPercent',
            value:
                calculationType === DiscountCalculationTypesEnum.pct
                    ? (discountSetting.percent ?? '')
                    : discountSetting?.amount && isMoney(discountSetting.amount)
                      ? moneyToBigNumber(discountSetting.amount).toString()
                      : ''
        });
    };

    return {
        columns: [
            {
                field: 'adjustmentName',
                headerName: '',
                editable: false,
                minWidth: 53
            },
            useCalculationTypeColDef({
                name,
                prop: 'calculationType',
                locale,
                width: 120,
                readOnly: false,
                calculationTypes,
                setRows,
                rates,
                onChange
            }),
            useDiscountColDef({
                name,
                prop: 'itemPercent',
                locale,
                width: 110,
                readOnly: false,
                setIsRowValid,
                calculator,
                rows,
                setRows: setRows
            }),
            {
                field: 'itemQuantity',
                headerName: '',
                editable: false,
                minWidth: 100
            },
            {
                field: 'duration',
                headerName: '',
                editable: false,
                minWidth: 80
            },
            {
                field: 'area',
                headerName: '',
                editable: false,
                minWidth: 115
            },
            useMoneyColDef({
                name,
                prop: 'baseTotal',
                locale,
                width: 140,
                readOnly: shouldCalculate,
                setIsRowValid,
                calculator,
                classNames: 'MuiDataGrid-cell--textBold',
                rows,
                setRows: setRows
            })
        ],
        isRowValid
    };
};

export const getCostSummary = (adjustmentsData: LineCalculationInfo[], costLine: CostLine) => {
    const discounts = getSumOfDiscounts(adjustmentsData, costLine.lineCurrency);
    return {
        ...costLine.costSummary,
        discount: discounts
    };
};

export const DiscountGrid = (props) => {
    const {
        name,
        allowedCalculationTypes,
        costLine,
        setCostLine,
        adjustments,
        defaultCurrencyCode,
        autoCalculate,
        selectedRateDetails,
        columnVisibility,
        expandedDetail,
        setExpandedDetail,
        adjustmentSetting,
        discountLevel,
        disableActions
    } = props;
    const { l10n } = useL10n();
    const calculator = useCalculator(defaultCurrencyCode, allowedCalculationTypes, autoCalculate);
    const { rowsToAdjustments } = useCostLineAdapter();

    const previousBaseTotalRef = useRef(costLine.lineCalculationInfo.calculationInfo.baseTotal);

    const onDynamicRowAdd = (apiRef) => {
        if (
            apiRef.current.getRowMode(0) == 'view' &&
            (discountLevel ? discountLevel === discountLevelTypes.Line : true)
        ) {
            apiRef.current.startRowEditMode({ id: 0, fieldToFocus: 'calculationType' });
        }
        setExpandedDetail(false);
    };

    const rows = useMemo(() => {
        return adjustmentsToRows(
            adjustments,
            adjustmentSetting,
            costLine.lineCalculationInfo.calculationInfo.baseTotal,
            costLine.lineCurrency,
            calculator
        );
    }, [
        adjustmentSetting,
        adjustments,
        calculator,
        costLine.lineCalculationInfo.calculationInfo.baseTotal,
        costLine.lineCurrency
    ]);

    const updateRows = useCallback(
        (model: GridRowModel[]) => {
            const adjustmentsData = rowsToAdjustments(
                model as AdjustmentLine[],
                allowedCalculationTypes,
                adjustmentSetting,
                costLine.lineCalculationInfo.calculationInfo.baseTotal,
                autoCalculate
            );
            setCostLine({
                ...costLine,
                adjustments: adjustmentsData,
                costSummary: getCostSummary(adjustmentsData, costLine)
            });
        },
        [rowsToAdjustments, allowedCalculationTypes, adjustmentSetting, costLine, autoCalculate, setCostLine]
    );

    const deleteRow = (id) => {
        const adjustmentsData = cloneDeep(adjustments);
        adjustmentsData.splice(id, 1);
        setCostLine({
            ...costLine,
            adjustments: adjustmentsData,
            costSummary: getCostSummary(adjustmentsData, costLine)
        });
    };

    const handleProcessRowUpdate = useCallback(
        (newRow: GridRowModel, oldRow: GridRowModel) => {
            const updatedRows = rows.map((row) => (row.id === oldRow.id ? { ...oldRow, ...newRow } : row));
            updateRows(updatedRows);
            return newRow;
        },
        [updateRows, rows]
    );

    useEffect(() => {
        const nextBaseTotal = costLine.lineCalculationInfo.calculationInfo.baseTotal;
        if (isEqual(previousBaseTotalRef.current, nextBaseTotal)) {
            return;
        }
        previousBaseTotalRef.current = nextBaseTotal;
        updateRows(rows);
    }, [costLine.lineCalculationInfo.calculationInfo.baseTotal, rows, updateRows]);

    const { columns, isRowValid } = useDiscountGridColumns(
        name,
        l10n.locale,
        allowedCalculationTypes,
        rows,
        updateRows,
        calculator,
        autoCalculate,
        selectedRateDetails,
        adjustmentSetting
    );

    return (
        <>
            <Typography sx={{ pl: '10px' }}>
                <I18n token="grid.header.discounts"></I18n>
            </Typography>
            {rows.length > 0 && (
                <DataGrid
                    id={'discountGrid'}
                    hideFooter
                    slots={{
                        noRowsOverlay: () => <></>
                    }}
                    enableInlineEditing={discountLevel !== discountLevelTypes.Booking}
                    append
                    hideHeader
                    defaultColumnVisibility={columnVisibility}
                    feature={FEATURE_CONSTANTS.orders}
                    rows={rows}
                    setRows={updateRows}
                    triggerEditMode={expandedDetail}
                    columns={columns}
                    isRowValid={(rowId) => isRowValid(rowId)}
                    forceDefaultColumnVisibility
                    processRowUpdate={handleProcessRowUpdate}
                    commitRowOnBlur
                    disableAdd={adjustments?.length > 0}
                    onDynamicRowAdd={onDynamicRowAdd}
                    onDelete={!disableActions && discountLevel !== discountLevelTypes.Booking ? deleteRow : undefined}
                />
            )}
        </>
    );
};
